/**
 * Hook que expone el modo de precios del tenant (PRICE-B).
 *
 * PRICE-A persiste `pricingMode` en `tenant.settings` con valores
 * `'single'` (legacy, una sola moneda por variant.price) o `'multi'`
 * (multi-currency con PriceLists activo). El frontend usa este flag
 * para ocultar UI de PriceLists cuando el comerciante no la necesita.
 *
 * Contrato:
 *   - Si `useTenantSettings()` aún no devolvió data o `config.pricingMode`
 *     viene undefined → `'single'`. Default conservador: tenants pre-PRICE-A
 *     ven la UI legacy intacta sin opt-in.
 *   - Cuando hay data → devuelve el valor persistido (`'single'` o `'multi'`).
 *
 * Pre-auth NO debe usar este hook — `useTenantSettings` requiere cookie JWT.
 */

import type { ResolvedTenantConfig } from './api';
import { useTenantSettings } from './queries';

export type PricingMode = 'single' | 'multi';

/**
 * Pura — extraída del hook para que los tests no necesiten
 * `QueryClientProvider`. Mismo patrón que `selectBrand` en
 * `useTenantBrand.ts`. Defaultea a `'single'` cuando el config aún no
 * cargó o cuando el tenant no tiene el flag seteado (backward-compat
 * absoluto: nadie pre-PRICE-A se rompe).
 */
export function selectPricingMode(
  config: ResolvedTenantConfig | undefined,
): PricingMode {
  if (!config) return 'single';
  return config.pricingMode === 'multi' ? 'multi' : 'single';
}

export function usePricingMode(): PricingMode {
  const { data } = useTenantSettings();
  return selectPricingMode(data?.config);
}
